import type { PurchaseMode } from '@/api/pay'
import type { StoreProduct, StoreProductType } from '@/utils/platform'

export type StoreProductContractInput = {
  packageId: string
  appleProductId?: string
  purchaseMode?: PurchaseMode
  isAutoRenew: boolean
  storeProduct?: StoreProduct
}

export type StoreProductContractResult =
  | { valid: true; expectedProductType: StoreProductType }
  | { valid: false; reason: string; expectedProductType?: StoreProductType }

const PACKAGE_MODE_CONTRACT: Record<string, PurchaseMode> = {
  single: 'consumable',
  monthly: 'fixed_term',
  quarterly: 'fixed_term',
  yearly: 'fixed_term',
  monthly_auto: 'auto_renewable',
}

export function shouldShowAutoRenewStatus(packageId?: string | null): boolean {
  return packageId === 'monthly_auto'
}

export function expectedStoreProductType(
  purchaseMode: PurchaseMode,
): StoreProductType {
  switch (purchaseMode) {
    case 'auto_renewable':
      return 'auto_renewable'
    case 'fixed_term':
      return 'non_renewable'
    case 'consumable':
      return 'consumable'
  }
}

export function validateStoreProductContract(
  input: StoreProductContractInput,
): StoreProductContractResult {
  const expectedMode = PACKAGE_MODE_CONTRACT[input.packageId]
  if (!input.purchaseMode || !expectedMode || input.purchaseMode !== expectedMode) {
    return { valid: false, reason: 'package_purchase_mode_mismatch' }
  }

  const shouldAutoRenew = input.purchaseMode === 'auto_renewable'
  if (input.isAutoRenew !== shouldAutoRenew) {
    return { valid: false, reason: 'package_auto_renew_flag_mismatch' }
  }

  if (shouldAutoRenew && input.packageId !== 'monthly_auto') {
    return { valid: false, reason: 'auto_renew_package_not_allowed' }
  }

  const expectedProductType = expectedStoreProductType(input.purchaseMode)
  if (!input.appleProductId || !input.storeProduct) {
    return {
      valid: false,
      reason: 'store_product_missing',
      expectedProductType,
    }
  }
  if (input.storeProduct.product_id !== input.appleProductId) {
    return {
      valid: false,
      reason: 'store_product_id_mismatch',
      expectedProductType,
    }
  }
  if (input.storeProduct.product_type !== expectedProductType) {
    return {
      valid: false,
      reason: 'store_product_type_mismatch',
      expectedProductType,
    }
  }
  if (
    shouldAutoRenew
    && (
      input.storeProduct.subscription_period_unit !== 'month'
      || input.storeProduct.subscription_period_value !== 1
    )
  ) {
    return {
      valid: false,
      reason: 'store_product_period_mismatch',
      expectedProductType,
    }
  }

  return { valid: true, expectedProductType }
}
